import { useState } from "preact/hooks";
import type { CompanySize, CompanyStatus, Industry } from "../../types";
import { SALES_OWNERS } from "../../types";
import { Modal } from "../ui/Modal";
import { store } from "../../store/store";
import { toast } from "../shell/Toast";

const INDUSTRIES: Industry[] = [
  "logistics",
  "retail",
  "fintech",
  "healthcare",
  "manufacturing",
  "travel",
  "e-commerce",
  "professional-services",
];

const SIZES: CompanySize[] = ["1-10", "11-50", "51-200", "201-500", "500+"];

export function NewCompanyModal({ onClose, onCreated }: { onClose: () => void; onCreated: (companyId: string) => void }) {
  const [name, setName] = useState("");
  const [industry, setIndustry] = useState<Industry>("retail");
  const [size, setSize] = useState<CompanySize>("11-50");
  const [country, setCountry] = useState("");
  const [accountOwner, setAccountOwner] = useState<string>(SALES_OWNERS[0]);
  const [status, setStatus] = useState<CompanyStatus>("Prospect");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);

  function submit(e: Event) {
    e.preventDefault();
    if (!name.trim()) {
      setError("Company name is required.");
      return;
    }
    if (!country.trim()) {
      setError("Country is required.");
      return;
    }
    const company = store.addCompany({ name: name.trim(), industry, size, country: country.trim(), accountOwner, status, notes: notes.trim() || undefined });
    toast.success("Company created.");
    onCreated(company.id);
  }

  return (
    <Modal title="New Company" onClose={onClose} width={460}>
      <form class="form" onSubmit={submit}>
        {error && <p class="form-error">{error}</p>}
        <label>
          Company name
          <input data-ai-id="company-name" value={name} onInput={(e) => setName((e.target as HTMLInputElement).value)} />
        </label>
        <div class="form-row">
          <label>
            Industry
            <select data-ai-id="industry" value={industry} onChange={(e) => setIndustry((e.target as HTMLSelectElement).value as Industry)}>
              {INDUSTRIES.map((i) => (
                <option key={i} value={i}>
                  {i}
                </option>
              ))}
            </select>
          </label>
          <label>
            Company size
            <select data-ai-id="company-size" value={size} onChange={(e) => setSize((e.target as HTMLSelectElement).value as CompanySize)}>
              {SIZES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div class="form-row">
          <label>
            Country
            <input data-ai-id="country" value={country} onInput={(e) => setCountry((e.target as HTMLInputElement).value)} />
          </label>
          <label>
            Account owner
            <select data-ai-id="account-owner" value={accountOwner} onChange={(e) => setAccountOwner((e.target as HTMLSelectElement).value)}>
              {SALES_OWNERS.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </label>
        </div>
        <label>
          Status
          <select data-ai-id="account-status" value={status} onChange={(e) => setStatus((e.target as HTMLSelectElement).value as CompanyStatus)}>
            <option value="Prospect">Prospect</option>
            <option value="Active Customer">Active Customer</option>
            <option value="At Risk">At Risk</option>
            <option value="Churned">Churned</option>
          </select>
        </label>
        <label>
          Notes
          <textarea data-ai-id="notes" value={notes} onInput={(e) => setNotes((e.target as HTMLTextAreaElement).value)} />
        </label>
        <div class="form-actions">
          <button type="button" class="btn btn--secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" class="btn btn--primary" data-ai-id="submit-create-company">
            Create Company
          </button>
        </div>
      </form>
    </Modal>
  );
}
